/**
 * One ticket (TMMAPP-0142 U2 item 13, §G U2): `GET /v1/me/tickets/{number}`.
 * - Its number (`SC-1001`), title, status chip (TMM-B2; `WAITING_ON_YOU` reads "Waiting on you" in
 *   the warning tone, with a line asking for the person's reply), the description and its files.
 * - **A duplicate** reads "Merged into {number}" with that ticket's status. It offers "Open {number}"
 *   only when that ticket is in the person's own list (`canOpen`): the canonical ticket is usually
 *   someone else's, and reading it would 404 (M4).
 * - **The public updates**, oldest first: the person's own ("You"), the support team's (a `STAFF`
 *   update names no one: "The support team") and the system's (a status change), each with files.
 * - **Reply**: the one composer, with the attach control (the files are the person's own uploads to
 *   their Help & feedback session, copied onto the reply). Nothing changes while it sends (K2).
 * - **Close**, for a support case that isn't closed, after a confirm.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Link, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError } from '@/api/errors';
import { ticketLabel, ticketsApi, type Ticket, type TicketUpdate } from '@/api/tickets';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { PreviousIcon } from '@/components/icons';
import { StatusChip } from '@/components/StatusChip';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { useNotify } from '@/shell/NotifyProvider';
import { resolveTokens } from '@/theme/theme';
import { statusLabel } from '@/components/statusLabel';
import { Composer } from '../composer/Composer';
import { useAttachments } from '../composer/useAttachments';
import { TicketAttachments } from './TicketAttachments';
import { TICKET_TYPE_KEYS, ticketErrorCopy } from './ticketCopy';

const CLOSED: ReadonlySet<string> = new Set(['CLOSED', 'RESOLVED', 'DUPLICATE']);

/** The reply box, with files when the person's Help & feedback session is known. */
function ReplyBox({
  sessionId,
  onReply,
  notice,
}: {
  sessionId: number | null;
  onReply: (text: string, ids: number[]) => Promise<boolean>;
  notice: ReactNode;
}) {
  const { t } = useTranslation();
  const attachments = useAttachments({ sessionId: sessionId ?? 0 });
  return (
    <Composer
      label={t('support.tickets.replyLabel')}
      sendLabel={t('support.tickets.reply')}
      sendHeld={false}
      onSend={onReply}
      notice={notice}
      attachments={sessionId === null ? undefined : attachments}
    />
  );
}

export function TicketView({
  number,
  supportSessionId,
  canOpen,
  onBack,
  onOpenTicket,
  onChanged,
  onLoaded,
}: {
  number: number;
  /** The person's Help & feedback session: a reply's files are uploaded there first. */
  supportSessionId: number | null;
  /** The ticket is in the person's own list, so it can be opened. */
  canOpen: (number: number) => boolean;
  onBack: () => void;
  onOpenTicket: (number: number) => void;
  /** The ticket changed (a reply, closed): the list re-reads. */
  onChanged: () => void;
  /** The ticket was read: the panel shows the tab of its type (a link may open it before the list). */
  onLoaded?: ((ticket: Ticket) => void) | undefined;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const notify = useNotify();
  const zone = useMe().user.timezone ?? 'UTC';
  const headingId = useId();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const onLoadedRef = useRef(onLoaded);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
  });

  const load = useCallback(async () => {
    try {
      const read = await ticketsApi.get(number);
      setTicket(read);
      setLoadError(null);
      onLoadedRef.current?.(read);
    } catch (error) {
      setLoadError(error);
    }
  }, [number]);

  useEffect(() => {
    queueMicrotask(() => {
      setTicket(null);
      void load();
    });
  }, [load]);

  // The view opens with focus on its heading.
  const shown = ticket !== null || loadError !== null;
  useEffect(() => {
    if (shown) document.getElementById(headingId)?.focus();
  }, [shown, headingId]);

  const when = (instant: string) => {
    try {
      return formatInstant(instant, locale, zone);
    } catch {
      return instant;
    }
  };

  async function reply(text: string, ids: number[]): Promise<boolean> {
    setReplyError(null);
    try {
      setTicket(await ticketsApi.reply(number, text, ids));
      notify(t('support.tickets.replied'));
      onChanged();
      return true;
    } catch (error) {
      setReplyError(ticketErrorCopy(t, error, locale));
      return false;
    }
  }

  async function close() {
    setClosing(true);
    setCloseError(null);
    try {
      setTicket(await ticketsApi.close(number));
      setConfirmClose(false);
      notify(t('support.tickets.closed'));
      onChanged();
    } catch (error) {
      setCloseError(ticketErrorCopy(t, error, locale));
    } finally {
      setClosing(false);
    }
  }

  function author(update: TicketUpdate): string {
    if (update.authorKind === 'STAFF') return t('support.tickets.supportTeam');
    if (update.authorKind === 'SYSTEM') return t('support.tickets.system');
    return t('support.tickets.you');
  }

  const back = (
    <Box>
      <Button size="small" startIcon={<PreviousIcon />} onClick={onBack}>
        {t('support.tickets.back')}
      </Button>
    </Box>
  );

  if (ticket === null) {
    const missing = isApiError(loadError) && loadError.code === ErrorCodes.TICKET_NOT_FOUND;
    return (
      <Stack sx={{ gap: 1.5 }}>
        {back}
        <Typography
          component="h3"
          id={headingId}
          tabIndex={-1}
          sx={{ m: 0, fontSize: 15, fontWeight: 600 }}
        >
          {t('support.tickets.ticketHeading')}
        </Typography>
        {loadError === null ? (
          <Typography sx={{ fontSize: 14, color: tokens.text2 }}>{t('common.loading')}</Typography>
        ) : missing ? (
          <Alert severity="info">{t('support.tickets.notFound')}</Alert>
        ) : (
          <ErrorState
            variant="section"
            error={loadError}
            what={t('support.tickets.loadOneError')}
            onRetry={() => void load()}
          />
        )}
      </Stack>
    );
  }

  const label = ticketLabel(ticket);
  const canClose = ticket.type === 'SUPPORT_CASE' && !CLOSED.has(ticket.status);
  const canReply = ticket.status !== 'DUPLICATE';

  return (
    <Stack sx={{ gap: 1.5 }} data-ticket-view={ticket.number}>
      {back}
      <Stack sx={{ gap: 0.75 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            component="span"
            sx={{ fontSize: 12, fontWeight: 500, color: tokens.text2 }}
            data-ticket-label=""
          >
            {label} · {t(TICKET_TYPE_KEYS[ticket.type])}
          </Typography>
          <StatusChip domain="ticket" status={ticket.status} />
        </Stack>
        <Typography
          component="h3"
          id={headingId}
          tabIndex={-1}
          sx={{ m: 0, fontSize: 15, fontWeight: 600, overflowWrap: 'anywhere' }}
        >
          {ticket.title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: tokens.text2 }}>
          {t('support.tickets.updated', { when: when(ticket.updatedAt) })}
        </Typography>
      </Stack>
      {ticket.status === 'WAITING_ON_YOU' && (
        <Alert severity="warning" data-waiting-on-you="">
          {t('support.tickets.waitingOnYou')}
        </Alert>
      )}
      {ticket.duplicateOf !== null && (
        <Alert severity="info" data-duplicate-of={ticket.duplicateOf}>
          <Stack sx={{ gap: 0.5 }}>
            <span>
              {t('support.tickets.duplicate', {
                number: ticketLabel({ type: ticket.type, number: ticket.duplicateOf }),
              })}
              {ticket.duplicateOfStatus !== null &&
                ` · ${statusLabel(t, 'ticket', ticket.duplicateOfStatus)}`}
            </span>
            {canOpen(ticket.duplicateOf) && (
              <Box>
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    if (ticket.duplicateOf !== null) onOpenTicket(ticket.duplicateOf);
                  }}
                  sx={{ fontSize: 14 }}
                >
                  {t('support.tickets.openDuplicate', {
                    number: ticketLabel({ type: ticket.type, number: ticket.duplicateOf }),
                  })}
                </Link>
              </Box>
            )}
          </Stack>
        </Alert>
      )}
      <Box>
        <Typography component="h4" sx={{ m: 0, fontSize: 13, fontWeight: 600 }}>
          {t('support.tickets.description')}
        </Typography>
        <Typography
          sx={{ fontSize: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.5 }}
        >
          {ticket.description}
        </Typography>
        <TicketAttachments number={ticket.number} files={ticket.attachments} />
      </Box>
      <Box>
        <Typography component="h4" sx={{ m: 0, mb: 0.75, fontSize: 13, fontWeight: 600 }}>
          {t('support.tickets.updates')}
        </Typography>
        {ticket.updates.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: tokens.text2 }}>
            {t('support.tickets.noUpdates')}
          </Typography>
        ) : (
          <Stack component="ol" sx={{ listStyle: 'none', m: 0, p: 0, gap: 1 }}>
            {ticket.updates.map((update) => (
              <Box
                component="li"
                key={update.id}
                data-ticket-update={update.authorKind}
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: update.authorKind === 'MEMBER' ? tokens.selected : tokens.surface2,
                }}
              >
                <Typography component="p" sx={{ m: 0, fontSize: 12, fontWeight: 500 }}>
                  {author(update)}
                  <Box component="span" sx={{ color: tokens.text2, fontWeight: 400 }}>
                    {` · ${when(update.createdAt)}`}
                  </Box>
                </Typography>
                {update.toStatus !== null && (
                  <Typography sx={{ fontSize: 13, color: tokens.text2, mt: 0.25 }}>
                    {t('support.tickets.statusChanged', {
                      status: statusLabel(t, 'ticket', update.toStatus),
                    })}
                  </Typography>
                )}
                {update.body.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      mt: 0.25,
                    }}
                  >
                    {update.body}
                  </Typography>
                )}
                <TicketAttachments number={ticket.number} files={update.attachments} />
              </Box>
            ))}
          </Stack>
        )}
      </Box>
      {canReply && (
        <ReplyBox
          sessionId={supportSessionId}
          onReply={reply}
          notice={
            replyError === null ? null : (
              <Alert severity="warning" data-reply-error="">
                {replyError}
              </Alert>
            )
          }
        />
      )}
      {canClose && (
        <Box>
          <Button variant="outlined" size="small" onClick={() => setConfirmClose(true)}>
            {t('support.tickets.close')}
          </Button>
        </Box>
      )}
      {confirmClose && (
        <ConfirmDialog
          title={t('support.tickets.closeTitle', { number: label })}
          consequence={t('support.tickets.closeConsequence')}
          confirmLabel={t('support.tickets.close')}
          busy={closing}
          error={closeError}
          onConfirm={() => void close()}
          onCancel={() => setConfirmClose(false)}
        />
      )}
    </Stack>
  );
}
